import { sendRequest } from '@infrastructure/api/api';
import { UserAccountsResponse } from '../types/entity';
import ApiResponse from '@core/types/apiResponse';

export interface UserAccessPermission {
  action_code: string;
  department_id: number;
}

export interface LeadershipNavigation {
  show_section: boolean;
  pastor: boolean;
  finance_statistics: boolean;
  district_overseer: boolean;
}

export interface UserAccessWithLeadership {
  permissions: UserAccessPermission[];
  leadership: LeadershipNavigation;
}

export interface FetchUserAccessOptions {
  departmentId?: number;
  includeLeadership?: boolean;
}

let userAccountsInFlight: Promise<ApiResponse<UserAccountsResponse>> | null = null;
const userAccessInFlight = new Map<string, Promise<ApiResponse<UserAccessPermission[] | UserAccessWithLeadership>>>();

function buildUserAccessKey(
  scope: string,
  scopeRefId: number,
  actions: string[],
  options?: FetchUserAccessOptions
) {
  return JSON.stringify({
    scope,
    scopeRefId,
    departmentId: options?.departmentId ?? null,
    includeLeadership: options?.includeLeadership ?? false,
    actions: [...actions].sort(),
  });
}

function isWrappedUserAccess(
  data: UserAccessPermission[] | UserAccessWithLeadership
): data is UserAccessWithLeadership {
  return !Array.isArray(data) && Array.isArray((data as UserAccessWithLeadership).permissions);
}

/**
 * Fetches all entity accounts (nation, district, church) for the authenticated user
 * @param page - Page number for pagination
 * @param perPage - Number of results per page
 * @returns Promise<ApiResponse<UserAccountsResponse>>
 */
export const fetchUserAccounts = async (): Promise<ApiResponse<UserAccountsResponse>> => {
  if (!userAccountsInFlight) {
    userAccountsInFlight = sendRequest<any, UserAccountsResponse>(
      'GET',
      `/user/account/all`
    ).finally(() => {
      userAccountsInFlight = null;
    });
  }

  return userAccountsInFlight;
};

/**
 * Checks which action codes the authenticated user has access to
 * for a specific scope and entity, optionally filtered by department.
 *
 * When `includeLeadership` is true, the response is wrapped with a `leadership` block.
 */
export const fetchUserAccess = async (
  scope: string,
  scopeRefId: number,
  actions: string[],
  options?: FetchUserAccessOptions
): Promise<ApiResponse<UserAccessPermission[] | UserAccessWithLeadership>> => {
  const requestKey = buildUserAccessKey(scope, scopeRefId, actions, options);

  if (!userAccessInFlight.has(requestKey)) {
    const body: Record<string, unknown> = {
      scope,
      scope_ref_id: scopeRefId,
      actions,
    };
    if (options?.departmentId != null && options.departmentId > 0) {
      body.department_id = options.departmentId;
    }
    if (options?.includeLeadership) {
      body.include_leadership = true;
    }

    userAccessInFlight.set(
      requestKey,
      sendRequest<any, UserAccessPermission[] | UserAccessWithLeadership>('POST', '/user/access', body).finally(() => {
        userAccessInFlight.delete(requestKey);
      })
    );
  }

  return userAccessInFlight.get(requestKey)!;
};

export { isWrappedUserAccess };
